/**
 * SavedPlans.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { debugSavedPlans } from './debug.js'
import { Gas } from '../core/Gas.js'
import { Store } from '../store/Store.js'
import { PLAN_ERROR_CODES } from '../core/errors.js'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

function isNativePlatform() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
}

// Saved dive plans manager with localStorage persistence
const SAVED_PLANS_KEY = 'mvplan_saved_plans'

// App version injected by Vite at build time
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev'

export class SavedPlans {
  // Dive environment of a plan saved before the environment became part of a
  // saved plan: such a plan carries no `environment` key at all.
  //
  // The environment currently selected in the application must never be
  // applied to it. A wreck dive planned at sea and reloaded while the screen
  // is set on a mountain lake would be recomputed against another water
  // column and another surface pressure, so with a decompression that is not
  // the one the plan was saved with.
  //
  // Those plans were computed when the environment did not reach the engine,
  // which was exactly equivalent to sea level in sea water: that is the
  // documented fallback. It is written here as a literal, and deliberately not
  // read from Store.DEFAULT_WATER_TYPE, so that changing the default of the
  // application can never change what an already saved plan means.
  static LEGACY_ENVIRONMENT = Object.freeze({ altitude: 0, waterType: 'salt' })

  // Altitude range accepted when a saved plan is read back, in metres.
  // The Settings screen only offers 0 to 4000 m; the range read back is wider
  // so a hand written plan is not refused for a real site (the shore of the
  // Dead Sea is 430 m below sea level), while a value that cannot be a dive
  // site - corrupted storage, an altitude stored in feet - is refused instead
  // of being planned: at 3000 m the surface pressure is 0.70 bar instead of
  // 1.01, and replanning that dive at sea level takes decompression away.
  static MIN_ALTITUDE = -500
  static MAX_ALTITUDE = 9000

  /**
   * Dive environment a saved plan must be recomputed in.
   *
   * Returns { environment, legacy, errorKey, reason }:
   *   environment  altitude and water type to plan with, always an object
   *   legacy       true when the plan carried no environment at all
   *   errorKey     null when usable, otherwise the i18n key of the refusal
   *   reason       machine readable refusal reason, already logged
   *
   * Never throws: the same call feeds the plan list and the load.
   */
  static readEnvironment(plan) {
    const planId = plan?.id
    const stored = plan?.environment
    const environment = { ...SavedPlans.LEGACY_ENVIRONMENT }

    if (stored === undefined || stored === null) {
      debugSavedPlans('readEnvironment: plan id=%o carries no environment, read back at sea level in sea water', planId)
      return { environment, legacy: true, errorKey: null, reason: null }
    }

    if (typeof stored !== 'object' || Array.isArray(stored)) {
      console.error('SavedPlans.readEnvironment: plan id=%o stores an unusable environment %o (%s), it cannot be recomputed',
        planId, stored, typeof stored)
      return { environment, legacy: false, errorKey: PLAN_ERROR_CODES.INVALID_CONFIG, reason: 'environment_not_an_object' }
    }

    // Water type: the weight of the water column. 40 m weigh 4.04 bar in sea
    // water and 3.92 bar in fresh water, so reading it wrong changes the
    // decompression itself, never just the display.
    if (stored.waterType !== undefined && stored.waterType !== null) {
      let waterType = stored.waterType
      if (typeof waterType === 'string' &&
          !Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, waterType)) {
        // A density written as a string ("10300"): the engine only accepts
        // preset keys in string form, so it is read as the number it is
        // instead of costing the diver the site that was selected.
        const parsed = Number(waterType)
        if (Store.isUsableDensity(parsed)) {
          console.warn('SavedPlans.readEnvironment: plan id=%o stores its water density as the string %o, read as %o g/l',
            planId, waterType, parsed)
          waterType = parsed
        }
      }
      try {
        // Logs the reason and throws when the value is neither a known preset
        // nor a density within the accepted salinity range.
        Gas.waterDensity(waterType)
      } catch (e) {
        console.error('SavedPlans.readEnvironment: plan id=%o has an unusable water type %o (%s), refused rather than planned in another water',
          planId, stored.waterType, e.message)
        return { environment, legacy: false, errorKey: PLAN_ERROR_CODES.INVALID_CONFIG, reason: 'water_type_unusable' }
      }
      environment.waterType = waterType
    } else {
      debugSavedPlans('readEnvironment: plan id=%o carries no water type, sea water assumed', planId)
    }

    // Altitude: drives the surface pressure, so it drives the whole ascent.
    if (stored.altitude !== undefined && stored.altitude !== null) {
      let altitude = stored.altitude
      if (typeof altitude === 'string' && altitude.trim() !== '') {
        const parsed = Number(altitude)
        if (Number.isFinite(parsed)) {
          console.warn('SavedPlans.readEnvironment: plan id=%o stores its altitude as the string %o, read as %o m',
            planId, altitude, parsed)
          altitude = parsed
        }
      }
      if (typeof altitude !== 'number' || !Number.isFinite(altitude) ||
          altitude < SavedPlans.MIN_ALTITUDE || altitude > SavedPlans.MAX_ALTITUDE) {
        console.error('SavedPlans.readEnvironment: plan id=%o has an unusable altitude %o, expected a number within [%o, %o] m',
          planId, stored.altitude, SavedPlans.MIN_ALTITUDE, SavedPlans.MAX_ALTITUDE)
        return { environment, legacy: false, errorKey: PLAN_ERROR_CODES.INVALID_CONFIG, reason: 'altitude_unusable' }
      }
      environment.altitude = altitude
    } else {
      debugSavedPlans('readEnvironment: plan id=%o carries no altitude, read back at sea level', planId)
    }

    // Gas temperature only feeds the gas density alert, never the
    // decompression: an unusable value is dropped with a log instead of
    // refusing a plan whose decompression is perfectly defined.
    if (stored.gasTemp !== undefined && stored.gasTemp !== null) {
      if (typeof stored.gasTemp === 'number' && Number.isFinite(stored.gasTemp)) {
        environment.gasTemp = stored.gasTemp
      } else {
        console.warn('SavedPlans.readEnvironment: plan id=%o has an unusable gas temperature %o, the temperature set in the application is kept',
          planId, stored.gasTemp)
      }
    }

    return { environment, legacy: false, errorKey: null, reason: null }
  }

  /**
   * Check a gas list read back from storage.
   * Returns the list, or null when it cannot be breathed - the reason is
   * logged here or by Gas.validate, which is called on every entry.
   * `label` names the list in the logs: 'gases' or 'diluents'.
   */
  static validateGasList(list, label, planId) {
    if (!Array.isArray(list) || list.length === 0) {
      console.error('SavedPlans.validateGasList: plan id=%o has no usable %s list (%o)', planId, label, list)
      return null
    }

    for (let i = 0; i < list.length; i++) {
      const gas = list[i]
      if (!gas || typeof gas !== 'object' || Array.isArray(gas)) {
        console.error('SavedPlans.validateGasList: plan id=%o, %s[%d] is not a gas object (%o)', planId, label, i, gas)
        return null
      }
      if (typeof gas.id !== 'number' || !Number.isFinite(gas.id)) {
        console.error('SavedPlans.validateGasList: plan id=%o, %s[%d] "%o" has no usable id (%o), a segment could no longer name the mix it is planned with',
          planId, label, i, gas.name, gas.id)
        return null
      }
      try {
        // Logs its own reason (fO2 out of range, inconsistent fN2...) and throws
        Gas.validate(gas)
      } catch (e) {
        console.error('SavedPlans.validateGasList: plan id=%o, %s[%d] "%o" is not a usable mix (%s)',
          planId, label, i, gas.name, e.message)
        return null
      }
    }

    return list
  }

  /**
   * Check a saved plan before it reaches the store, and return the parts the
   * store must take from it.
   *
   * Nothing is repaired silently here: a plan whose segments, gases,
   * conservatism or environment cannot be trusted is refused as a whole,
   * because loading it half way would mix the depths of one dive with the
   * mixes, the gradient factors or the water of another.
   *
   * Returns { valid: false, reason, errorKey } or
   * { valid: true, segments, gases, diluents, settings, ccr, environment,
   *   legacyEnvironment }, where `diluents` is null when the plan carries none
   * and does not need any (open circuit).
   */
  static validateForLoad(plan) {
    const planId = plan?.id
    const refuse = (reason, errorKey, detail) => {
      console.error('SavedPlans.validateForLoad: plan id=%o refused (%s): %s', planId, reason, detail)
      return { valid: false, reason, errorKey }
    }

    if (!plan || typeof plan !== 'object' || Array.isArray(plan)) {
      return refuse('plan_not_an_object', PLAN_ERROR_CODES.INVALID_CONFIG, `value=${JSON.stringify(plan)}`)
    }

    // Segments: the dive itself.
    if (!Array.isArray(plan.segments) || plan.segments.length === 0) {
      return refuse('no_segments', PLAN_ERROR_CODES.NO_SEGMENTS, `segments=${JSON.stringify(plan.segments)}`)
    }
    for (let i = 0; i < plan.segments.length; i++) {
      const segment = plan.segments[i]
      if (!segment || typeof segment !== 'object' || Array.isArray(segment)) {
        return refuse('segment_not_an_object', PLAN_ERROR_CODES.NO_SEGMENTS,
          `segments[${i}]=${JSON.stringify(segment)}`)
      }
      if (typeof segment.depth !== 'number' || !Number.isFinite(segment.depth) || segment.depth < 0) {
        return refuse('segment_depth_unusable', PLAN_ERROR_CODES.INVALID_DEPTH,
          `segments[${i}].depth=${JSON.stringify(segment.depth)}`)
      }
      if (typeof segment.time !== 'number' || !Number.isFinite(segment.time) || segment.time < 0) {
        return refuse('segment_time_unusable', PLAN_ERROR_CODES.INVALID_TIME,
          `segments[${i}].time=${JSON.stringify(segment.time)}`)
      }
      if (segment.gasId !== undefined && (typeof segment.gasId !== 'number' || !Number.isFinite(segment.gasId))) {
        // Display only: the engine picks the mix by MOD, the screen resolves
        // segment.gasId to name it. An unusable id costs the name, not the plan.
        console.warn('SavedPlans.validateForLoad: plan id=%o, segments[%d] has an unusable gas id (%o), the segment will be shown with the first enabled gas',
          planId, i, segment.gasId)
      }
    }

    // Breathing gases: also the bailout gases of a rebreather plan.
    // Refused rather than replaced by the ones currently on screen: the
    // segments were saved pointing at these mixes, and planning the same
    // depths on another gas list is exactly how a diver ends up breathing a
    // mix that was never planned.
    const gases = SavedPlans.validateGasList(plan.gases, 'gases', planId)
    if (!gases) {
      return refuse('gases_unusable', PLAN_ERROR_CODES.INVALID_GAS, 'the plan carries no usable gas list')
    }

    // Settings: merged over the ones of the application, so only the gradient
    // factors are checked here - they are the conservatism of the plan, and
    // the figure the saved plans list displays.
    if (!plan.settings || typeof plan.settings !== 'object' || Array.isArray(plan.settings)) {
      return refuse('settings_not_an_object', PLAN_ERROR_CODES.INVALID_CONFIG,
        `settings=${JSON.stringify(plan.settings)}`)
    }
    const settings = { ...plan.settings }
    for (const key of ['gfLow', 'gfHigh']) {
      const stored = settings[key]
      if (typeof stored !== 'number' || !Number.isFinite(stored) || stored <= 0) {
        return refuse(`${key}_unusable`, PLAN_ERROR_CODES.INVALID_GF, `settings.${key}=${JSON.stringify(stored)}`)
      }
      if (stored > 1) {
        // Same repair as Store.loadState: some versions persisted the gradient
        // factors as percentages (30) instead of fractions (0.30).
        const asFraction = stored / 100
        if (asFraction > 1) {
          return refuse(`${key}_out_of_range`, PLAN_ERROR_CODES.INVALID_GF, `settings.${key}=${JSON.stringify(stored)}`)
        }
        console.warn('SavedPlans.validateForLoad: plan id=%o stores %s as the percentage %o, read as %o',
          planId, key, stored, asFraction)
        settings[key] = asFraction
      }
    }

    // Rebreather block. A plan that carries none is an open circuit plan: the
    // rebreather currently switched on in the application must not be applied
    // to it, so the mode is forced to open circuit instead of being inherited.
    let ccr = plan.ccr
    if (!ccr || typeof ccr !== 'object' || Array.isArray(ccr)) {
      console.warn('SavedPlans.validateForLoad: plan id=%o carries no rebreather block (%o), loaded as an open circuit plan',
        planId, plan.ccr)
      ccr = { enabled: false }
    }

    // Loop flushes are part of what the plan was computed with, so a plan that
    // declares none has none. Without this line the store spread below keeps
    // the flushes currently on screen, and a plan saved before this feature
    // existed would come back flushing its loop at depths its author never
    // chose - with a decompression to match.
    ccr = { ...ccr, flushes: Array.isArray(ccr.flushes) ? ccr.flushes : [] }

    // Diluents: the breathing mix of a rebreather plan, unused by an open
    // circuit one - where the plan screen still needs a list to display.
    const diluents = SavedPlans.validateGasList(plan.diluents, 'diluents', planId)
    if (!diluents) {
      if (ccr.enabled) {
        return refuse('diluents_unusable', PLAN_ERROR_CODES.NO_DILUENT,
          'a rebreather plan cannot be loaded without the diluents it was planned with')
      }
      console.warn('SavedPlans.validateForLoad: plan id=%o has no usable diluent list, the diluents of the application are kept: they take no part in an open circuit plan',
        planId)
    }

    // Water and altitude the plan was computed in (readEnvironment logs why it
    // refuses, and falls back to sea level in sea water for the plans saved
    // before the environment was part of a saved plan).
    const env = SavedPlans.readEnvironment(plan)
    if (env.errorKey) {
      return refuse(env.reason, env.errorKey, 'the environment of the plan cannot be read back')
    }

    return {
      valid: true,
      segments: plan.segments,
      gases,
      diluents,
      settings,
      ccr,
      environment: env.environment,
      legacyEnvironment: env.legacy
    }
  }

  static getAll() {
    try {
      const saved = localStorage.getItem(SAVED_PLANS_KEY)
      const plans = saved ? JSON.parse(saved) : []
      debugSavedPlans('getAll: loaded %d plans', plans.length)
      return plans
    } catch (e) {
      console.warn('Failed to load saved plans:', e)
      debugSavedPlans('getAll: failed to load - %s', e.message)
      return []
    }
  }

  static save(name, state, results) {
    const plans = this.getAll()

    // The environment is part of the plan, not a screen preference. Without
    // it, reloading the plan while the application is set on another site
    // recomputes it against another water column and another surface
    // pressure, and the runtime frozen in the list stops matching the dive.
    let environment
    if (state.environment && typeof state.environment === 'object' && !Array.isArray(state.environment)) {
      environment = { ...state.environment }
    } else {
      console.warn('SavedPlans.save: the state carries no environment (%o), the plan is saved at sea level in sea water',
        state.environment)
      environment = { ...SavedPlans.LEGACY_ENVIRONMENT }
    }

    const plan = {
      id: Date.now(),
      name: name || this.generateName(state),
      createdAt: new Date().toISOString(),
      // Save all relevant state
      segments: state.segments,
      gases: state.gases,
      diluents: state.diluents,
      settings: state.settings,
      ccr: state.ccr,
      environment,
      // Save summary info for display
      summary: this.extractSummary(state, results)
    }
    plans.unshift(plan) // Add at beginning
    this.saveAll(plans)
    debugSavedPlans('save: saved plan id=%d name="%s"', plan.id, plan.name)
    return plan.id
  }

  static generateName(state) {
    const maxDepth = Math.max(...state.segments.map(s => s.depth))
    const totalTime = state.segments.reduce((sum, s) => sum + s.time, 0)
    const date = new Date().toLocaleDateString()
    return `${maxDepth}m/${totalTime}min - ${date}`
  }

  static extractSummary(state, results) {
    const maxDepth = Math.max(...state.segments.map(s => s.depth))
    const bottomTime = state.segments.reduce((sum, s) => sum + s.time, 0)
    const plan = results?.ccr || results?.bailout
    return {
      maxDepth,
      bottomTime,
      runtime: plan?.runtime ? Math.ceil(plan.runtime) : null,
      tts: plan?.tts || null,
      mode: state.ccr.enabled ? 'CCR' : 'OC',
      gf: `${Math.round(state.settings.gfLow * 100)}/${Math.round(state.settings.gfHigh * 100)}`
    }
  }

  static saveAll(plans) {
    try {
      localStorage.setItem(SAVED_PLANS_KEY, JSON.stringify(plans))
    } catch (e) {
      console.warn('Failed to save plans:', e)
    }
  }

  static get(id) {
    return this.getAll().find(p => p.id === id)
  }

  static rename(id, newName) {
    const plans = this.getAll()
    const plan = plans.find(p => p.id === id)
    if (!plan) {
      console.error('SavedPlans.rename: plan not found, id=%o, nothing renamed', id)
      return
    }
    plan.name = newName
    this.saveAll(plans)
  }

  static delete(id) {
    const plans = this.getAll().filter(p => p.id !== id)
    this.saveAll(plans)
    debugSavedPlans('delete: removed plan id=%d', id)
  }

  static duplicate(id) {
    const plans = this.getAll()
    const original = plans.find(p => p.id === id)
    if (!original) {
      console.error('SavedPlans.duplicate: plan not found, id=%o, nothing duplicated', id)
      return null
    }
    // Deep copy: the copy carries the whole plan, environment included
    const copy = {
      ...JSON.parse(JSON.stringify(original)),
      id: Date.now(),
      name: `${original.name} (copy)`,
      createdAt: new Date().toISOString()
    }
    plans.unshift(copy)
    this.saveAll(plans)
    debugSavedPlans('duplicate: plan id=%o copied as id=%o', id, copy.id)
    return copy.id
  }

  static async exportToJson(id) {
    const plan = this.get(id)
    if (!plan) {
      console.error('exportToJson: plan not found, id=' + id)
      return
    }

    const exportData = {
      _generator: 'mvplan',
      _version: APP_VERSION,
      _exportedAt: new Date().toISOString(),
      ...plan
    }
    const json = JSON.stringify(exportData, null, 2)
    const filename = `${plan.name.replace(/[^a-z0-9]/gi, '_')}.json`

    if (isNativePlatform()) {
      try {
        const base64 = btoa(unescape(encodeURIComponent(json)))
        const result = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        })
        await Share.share({
          title: filename,
          url: result.uri,
        })
        return
      } catch (e) {
        if (e.message?.includes('canceled') || e.message?.includes('cancelled')) {
          return
        }
        console.error('Native JSON export failed:', e)
      }
    }

    // Web fallback: <a download>
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 1000)
  }

  static importFromJson(jsonString) {
    try {
      const plan = JSON.parse(jsonString)
      // Validate everything the planner will breathe, exactly as a load does:
      // a plan that cannot be loaded must not enter the list either, or the
      // diver only finds out when the plan screen refuses to open.
      // validateForLoad logs the precise reason.
      const check = SavedPlans.validateForLoad(plan)
      if (!check.valid) {
        throw new Error(`Invalid plan format (${check.reason})`)
      }
      // Assign new ID and date
      plan.id = Date.now()
      plan.createdAt = new Date().toISOString()
      plan.name = plan.name || 'Imported plan'

      const plans = this.getAll()
      plans.unshift(plan)
      this.saveAll(plans)
      debugSavedPlans('importFromJson: imported plan id=%d name="%s"', plan.id, plan.name)
      return plan.id
    } catch (e) {
      console.error('Failed to import plan:', e)
      debugSavedPlans('importFromJson: failed - %s', e.message)
      return null
    }
  }

  /**
   * Load a saved plan into the current state.
   *
   * Returns { ok: true, plan, legacyEnvironment } on success, or
   * { ok: false, reason, errorKey } when the plan was refused. Every refusal
   * is logged with its reason before returning, and leaves the store
   * untouched: a half loaded plan is a dive planned on someone else's gases.
   */
  static loadIntoStore(id, store) {
    const plan = this.get(id)
    if (!plan) {
      console.error('SavedPlans.loadIntoStore: plan id=%o not found, nothing loaded', id)
      return { ok: false, reason: 'plan_not_found', errorKey: null }
    }

    const check = SavedPlans.validateForLoad(plan)
    if (!check.valid) {
      // validateForLoad logged the exact reason
      debugSavedPlans('loadIntoStore: plan id=%o refused (%s)', id, check.reason)
      return { ok: false, reason: check.reason, errorKey: check.errorKey }
    }

    // Update store with saved plan data
    store.state.segments = check.segments
    store.state.gases = check.gases
    if (check.diluents) {
      store.state.diluents = check.diluents
    }
    store.state.settings = { ...store.state.settings, ...check.settings }
    store.state.ccr = { ...store.state.ccr, ...check.ccr }
    // Water and altitude come from the plan, never from what the application
    // currently shows: check.environment always carries the altitude and the
    // water type, either the ones saved with the plan or the sea level / sea
    // water pair the plans predating the environment were computed with.
    store.state.environment = { ...store.state.environment, ...check.environment }
    store.state.results = { ccr: null, bailout: null }

    store.saveState()
    store.notify()
    if (check.legacyEnvironment) {
      console.warn('SavedPlans.loadIntoStore: plan id=%o was saved before the environment was part of a plan, it is recomputed at sea level in sea water (%o)',
        id, SavedPlans.LEGACY_ENVIRONMENT)
    }
    debugSavedPlans('loadIntoStore: loaded plan id=%o name="%s" environment=%o', id, plan.name, check.environment)
    return { ok: true, plan, legacyEnvironment: check.legacyEnvironment }
  }
}
